import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { emojiAPI, subscriptionAPI } from '@/lib/api';
import { ArrowLeft, Sparkles, Wand2, Download, Loader2, Eye, Image, BookOpen, Smile, Upload, Camera } from 'lucide-react';
import { toast } from 'sonner';

// Emoji categories with common emojis
const EMOJI_CATEGORIES = {
  'People': ['👩', '👨', '👶', '👴', '👵', '👤', '👥', '🧑‍🎨', '😀', '😢', '😎', '🥰', '😱', '🤔'],
  'Nature': ['🌅', '🌄', '🌇', '🌃', '🌙', '⭐', '🌟', '☀️', '🌈', '⛈️', '❄️', '🌸', '🌺', '🌻', '🌹', '🍂'],
  'Places': ['🏖️', '🏔️', '🌲', '🏜️', '🌊', '🏙️', '🏰', '🗼', '🌋', '🏝️', '🌾'],
  'Animals': ['🐱', '🐶', '🦁', '🦋', '🦅', '🐴', '🦄', '🐉', '🐺', '🦊', '🐻', '🦌'],
  'Objects': ['🎨', '📷', '🖼️', '✨', '💫', '🔥', '💎', '👑', '⚔️', '🎭', '🎪', '🎡'],
  'Moods': ['🖤', '💜', '💙', '💚', '💛', '🤍', '🌀', '💀', '👻'],
  'Era': ['⏳', '🔮', '🏛️', '🚀', '🎩', '⚙️']
};

const EmojiStudio = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedEmojis, setSelectedEmojis] = useState('');
  const [translatedPrompt, setTranslatedPrompt] = useState('');
  const [meanings, setMeanings] = useState([]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [credits, setCredits] = useState(null);
  const [activeTab, setActiveTab] = useState('create');
  
  // Image to Emoji state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [suggestedEmojis, setSuggestedEmojis] = useState('');
  const [imageDescription, setImageDescription] = useState('');

  useEffect(() => {
    loadRecipes();
    loadCredits();
  }, []);

  const loadRecipes = async () => {
    try {
      const data = await emojiAPI.getRecipes();
      setRecipes(data.recipes || []);
    } catch (error) {
      console.log('Could not load recipes');
    }
  };

  const loadCredits = async () => {
    try {
      const status = await subscriptionAPI.getStatus();
      setCredits(status);
    } catch (error) {
      console.log('Could not load credits');
    }
  };

  const addEmoji = (emoji) => {
    setSelectedEmojis(prev => prev + emoji);
    setTranslatedPrompt(''); // Clear previous translation
    setGeneratedImage(null);
  };

  const removeLastEmoji = () => {
    const emojis = [...selectedEmojis];
    emojis.pop();
    setSelectedEmojis(emojis.join(''));
    setTranslatedPrompt('');
    setGeneratedImage(null);
  };

  const clearEmojis = () => {
    setSelectedEmojis('');
    setTranslatedPrompt('');
    setMeanings([]);
    setGeneratedImage(null);
  };

  const handleEmojiInput = (e) => {
    setSelectedEmojis(e.target.value);
    setTranslatedPrompt('');
    setGeneratedImage(null);
  };

  const useRecipe = (recipe) => {
    setSelectedEmojis(recipe.emojis);
    setTranslatedPrompt('');
    setGeneratedImage(null);
    toast.success(`Loaded "${recipe.name}" recipe`);
  };

  const translateEmojis = async () => {
    if (!selectedEmojis) {
      toast.error('Add some emojis first!');
      return;
    }
    
    setIsTranslating(true);
    try {
      const result = await emojiAPI.translate(selectedEmojis);
      setTranslatedPrompt(result.prompt);
      setMeanings(result.meanings || []);
      toast.success('Prompt generated!');
    } catch (error) {
      toast.error('Translation failed');
    } finally {
      setIsTranslating(false);
    }
  };

  const generateImage = async () => {
    if (!selectedEmojis) {
      toast.error('Add some emojis first!');
      return;
    }

    setIsGenerating(true);
    setGeneratedImage(null);
    
    try {
      const result = await emojiAPI.generate(selectedEmojis);
      setGeneratedImage(result.image);
      setTranslatedPrompt(result.prompt);
      toast.success('Image generated!');
      loadCredits(); // Refresh credits
    } catch (error) {
      const msg = error.response?.data?.detail || 'Generation failed';
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = () => {
    if (!generatedImage) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${generatedImage}`;
    link.download = `emoji-art-${Date.now()}.png`;
    link.click();
  };

  // Image to Emoji functions
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setUploadedImage({ preview: e.target.result, base64 });
      setSuggestedEmojis('');
      setImageDescription('');
    };
    reader.readAsDataURL(file);
  };

  const analyzeImage = async () => {
    if (!uploadedImage) {
      toast.error('Please upload an image first');
      return;
    }
    
    setIsAnalyzing(true);
    try {
      const result = await emojiAPI.imageToEmoji(uploadedImage.base64);
      setSuggestedEmojis(result.emojis);
      setImageDescription(result.description);
      toast.success('Image analyzed!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Analysis failed';
      toast.error(msg);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const useSuggestedEmojis = () => {
    setSelectedEmojis(suggestedEmojis);
    setActiveTab('create');
    toast.success('Emojis loaded! Click Preview or Generate');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900">
      {/* Navigation */}
      <nav className="bg-black/20 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-white/10">
            <ArrowLeft className="h-5 w-5 mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Smile className="h-7 w-7 text-yellow-400" />
            Emoji Prompt Studio
          </h1>
          <div className="text-sm text-white/70">
            {credits && (
              <span>🖼️ {credits.image_credits} credits</span>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-white/10 mb-8">
            <TabsTrigger value="create" className="text-white data-[state=active]:bg-purple-600">
              <Wand2 className="h-4 w-4 mr-2" /> Create
            </TabsTrigger>
            <TabsTrigger value="image2emoji" className="text-white data-[state=active]:bg-purple-600">
              <Camera className="h-4 w-4 mr-2" /> Image to Emoji
            </TabsTrigger>
            <TabsTrigger value="recipes" className="text-white data-[state=active]:bg-purple-600">
              <BookOpen className="h-4 w-4 mr-2" /> Recipes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="create">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left: Emoji Selection */}
              <div className="space-y-6">
                {/* Selected Emojis Display */}
                <Card className="p-6 bg-white/10 border-white/20">
                  <label className="text-sm text-white/70 mb-2 block">Your Emoji Prompt</label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={selectedEmojis}
                      onChange={handleEmojiInput}
                      placeholder="Click emojis below or type here..."
                      className="text-3xl h-16 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                      data-testid="emoji-input"
                    />
                    <Button 
                      variant="outline" 
                      onClick={removeLastEmoji}
                      className="h-16 px-4 border-white/20 text-white hover:bg-white/10"
                    >
                      ⌫
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={clearEmojis}
                      className="h-16 px-4 border-white/20 text-white hover:bg-white/10"
                    >
                      ✕
                    </Button>
                  </div>
                  {selectedEmojis && (
                    <p className="text-white/50 text-sm mt-2">
                      {selectedEmojis.length} emojis selected
                    </p>
                  )}
                </Card>

                {/* Emoji Picker */}
                <Card className="p-6 bg-white/10 border-white/20">
                  <h3 className="text-lg font-semibold text-white mb-4">Pick Your Emojis</h3>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
                    {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
                      <div key={category}>
                        <label className="text-sm text-white/60 mb-2 block">{category}</label>
                        <div className="flex flex-wrap gap-2">
                          {emojis.map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => addEmoji(emoji)}
                              className="text-2xl p-2 rounded-lg hover:bg-white/20 transition-colors"
                              data-testid={`emoji-btn-${emoji}`}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Right: Preview & Generate */}
              <div className="space-y-6">
                {/* Action Buttons */}
                <div className="flex gap-4">
                  <Button
                    onClick={translateEmojis}
                    disabled={!selectedEmojis || isTranslating}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="preview-btn"
                  >
                    {isTranslating ? (
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                      <Eye className="h-5 w-5 mr-2" />
                    )}
                    Preview Prompt (Free)
                  </Button>
                  <Button
                    onClick={generateImage}
                    disabled={!selectedEmojis || isGenerating}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                    data-testid="generate-btn"
                  >
                    {isGenerating ? (
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="h-5 w-5 mr-2" />
                    )}
                    Generate Image
                  </Button>
                </div>

                {/* Translated Prompt Preview */}
                {translatedPrompt && (
                  <Card className="p-6 bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border-indigo-500/30">
                    <h3 className="text-sm font-medium text-indigo-300 mb-2">AI Prompt</h3>
                    <p className="text-white text-lg leading-relaxed">{translatedPrompt}</p>
                    {meanings.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-white/10">
                        <p className="text-xs text-white/50">
                          Detected: {meanings.join(' • ')}
                        </p>
                      </div>
                    )}
                  </Card>
                )}

                {/* Generated Image */}
                {generatedImage ? (
                  <Card className="p-6 bg-white/10 border-white/20">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Generated Image</h3>
                      <Button
                        onClick={downloadImage}
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        <Download className="h-4 w-4 mr-2" /> Download
                      </Button>
                    </div>
                    <img
                      src={`data:image/png;base64,${generatedImage}`}
                      alt="Generated from emojis"
                      className="w-full rounded-lg"
                      data-testid="generated-image"
                    />
                  </Card>
                ) : (
                  <Card className="p-12 bg-white/5 border-white/10 border-dashed flex flex-col items-center justify-center text-center">
                    <Image className="h-16 w-16 text-white/20 mb-4" />
                    <p className="text-white/40">Your generated image will appear here</p>
                    <p className="text-white/30 text-sm mt-2">Select emojis and click Generate</p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="recipes">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipes.map((recipe) => (
                <Card 
                  key={recipe.recipe_id} 
                  className="p-6 bg-white/10 border-white/20 hover:bg-white/15 transition-colors cursor-pointer"
                  onClick={() => useRecipe(recipe)}
                  data-testid={`recipe-${recipe.recipe_id}`}
                >
                  <div className="text-4xl mb-4 tracking-wider">{recipe.emojis}</div>
                  <h3 className="text-lg font-semibold text-white mb-2">{recipe.name}</h3>
                  <p className="text-white/60 text-sm">{recipe.description}</p>
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <span className="text-xs text-purple-300 bg-purple-500/20 px-2 py-1 rounded">
                      {recipe.category}
                    </span>
                  </div>
                </Card>
              ))}
            </div>
            
            {recipes.length === 0 && (
              <Card className="p-12 bg-white/5 border-white/10 text-center">
                <BookOpen className="h-16 w-16 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">Loading recipes...</p>
              </Card>
            )}
          </TabsContent>

          {/* Image to Emoji Tab */}
          <TabsContent value="image2emoji">
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Left: Image Upload */}
              <div className="space-y-6">
                <Card className="p-6 bg-white/10 border-white/20">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Upload className="h-5 w-5 text-blue-400" />
                    Upload an Image
                  </h3>
                  <p className="text-white/60 text-sm mb-4">
                    Upload any image and AI will suggest matching emojis that represent it
                  </p>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {uploadedImage ? (
                    <div className="space-y-4">
                      <img
                        src={uploadedImage.preview}
                        alt="Uploaded"
                        className="w-full rounded-lg max-h-64 object-contain bg-black/20"
                      />
                      <div className="flex gap-2">
                        <Button
                          onClick={() => fileInputRef.current?.click()}
                          variant="outline"
                          className="flex-1 border-white/20 text-white hover:bg-white/10"
                        >
                          <Upload className="h-4 w-4 mr-2" /> Change Image
                        </Button>
                        <Button
                          onClick={analyzeImage}
                          disabled={isAnalyzing}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          {isAnalyzing ? (
                            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                          ) : (
                            <Eye className="h-5 w-5 mr-2" />
                          )}
                          Analyze (Free)
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-white/20 rounded-lg p-12 text-center cursor-pointer hover:border-white/40 transition-colors"
                    >
                      <Camera className="h-16 w-16 text-white/30 mx-auto mb-4" />
                      <p className="text-white/50">Click to upload an image</p>
                      <p className="text-white/30 text-sm mt-2">JPG, PNG, or GIF</p>
                    </div>
                  )}
                </Card>
              </div>

              {/* Right: Results */}
              <div className="space-y-6">
                {suggestedEmojis ? (
                  <>
                    <Card className="p-6 bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-blue-500/30">
                      <h3 className="text-lg font-semibold text-blue-300 mb-4">Suggested Emojis</h3>
                      <div className="text-5xl tracking-widest mb-4">{suggestedEmojis}</div>
                      <p className="text-white/70 mb-6">{imageDescription}</p>
                      <Button
                        onClick={useSuggestedEmojis}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
                      >
                        <Sparkles className="h-5 w-5 mr-2" />
                        Use These Emojis to Generate
                      </Button>
                    </Card>
                    
                    <Card className="p-4 bg-white/5 border-white/10">
                      <p className="text-white/50 text-sm text-center">
                        Tip: You can edit the emojis in the Create tab before generating
                      </p>
                    </Card>
                  </>
                ) : (
                  <Card className="p-12 bg-white/5 border-white/10 border-dashed flex flex-col items-center justify-center text-center h-full">
                    <Smile className="h-16 w-16 text-white/20 mb-4" />
                    <p className="text-white/40">Suggested emojis will appear here</p>
                    <p className="text-white/30 text-sm mt-2">Upload an image and click Analyze</p>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EmojiStudio;
